import {
  BillingSubscriptionResponse,
  CheckoutSessionResponse,
  CreateOneTimeFeeAndProcessResponse,
  EnsureCustomerResponse,
  GrantFreePlaybookQuotaResponse,
  InvoicesResponse,
  OneTimeFeesResponse,
  OperationSuccessResponse,
  PlaybookQuotaInfoResponse,
  PortalSessionResponse,
  PricingModelStatsItemResponse,
  UpdatePlaybookCountResponse
} from "@/types/billing"

import { config } from "@/config"
import axios, { AxiosResponse } from "axios"
import { TCSBillingTier } from "@/constants/TCS/billing"

export const BillingAPI = {
  // Subscription management
  createCheckoutSession: async (token: string, organizationId: string, planTier: TCSBillingTier) => {
    try {
      const resp: AxiosResponse<CheckoutSessionResponse> = await axios({
        method: 'POST',
        url: `${config.apiUrl}/v1/billing/subscriptions:checkout`,
        headers: {
          Authorization: `Bearer ${token}`
        },
        data: { organizationId, planTier }
      })

      return { data: resp.data, err: undefined }
    } catch (error) {
      console.log('Error: ', error instanceof Error ? error.message : error)
      return { data: undefined, err: 'Unable to create checkout session' }
    }
  },

  /** Playbook-based subscription checkout (first-time or legacy migration). Backend must support this endpoint. */
  createPlaybookCheckoutSession: async (token: string, organizationId: string, playbookCount: number) => {
    try {
      const resp: AxiosResponse<CheckoutSessionResponse> = await axios({
        method: 'POST',
        url: `${config.apiUrl}/v1/billing/subscriptions:checkout-playbook`,
        headers: {
          Authorization: `Bearer ${token}`
        },
        data: { organizationId, playbookCount }
      })

      return { data: resp.data, err: undefined }
    } catch (error) {
      console.log('Error: ', error instanceof Error ? error.message : error)
      return { data: undefined, err: 'Unable to create playbook checkout session' }
    }
  },

  updatePlaybookCount: async (token: string, organizationId: string, playbookCount: number) => {
    try {
      const resp: AxiosResponse<UpdatePlaybookCountResponse> = await axios({
        method: 'POST',
        url: `${config.apiUrl}/v1/billing/subscriptions/${organizationId}/playbook-count`,
        headers: {
          Authorization: `Bearer ${token}`
        },
        data: { playbookCount }
      })

      return { data: resp.data, err: undefined }
    } catch (error) {
      console.log('Error: ', error instanceof Error ? error.message : error)
      return { data: undefined, err: 'Unable to update playbook count' }
    }
  },

  createPortalSession: async (token: string, organizationId: string) => {
    try {
      const resp: AxiosResponse<PortalSessionResponse> = await axios({
        method: 'POST',
        url: `${config.apiUrl}/v1/billing/portal-sessions:create`,
        headers: {
          Authorization: `Bearer ${token}`
        },
        data: { organizationId }
      })

      return { data: resp.data, err: undefined }
    } catch (error) {
      console.log('Error: ', error instanceof Error ? error.message : error)
      return { data: undefined, err: 'Unable to create portal session' }
    }
  },

  getBillingSubscription: async (token: string, organizationId: string) => {
    try {
      const resp: AxiosResponse<BillingSubscriptionResponse> = await axios({
        method: 'GET',
        url: `${config.apiUrl}/v1/billing/subscription/${organizationId}`,
        headers: {
          Authorization: `Bearer ${token}`
        }
      })

      return { data: resp.data, err: undefined }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const status = error.response?.status
        // 404 = no subscription yet; 403 = user org not yet resolved (new account edge case).
        // Both are treated as "no subscription" — not an error worth surfacing to the user.
        if (status === 404 || status === 403) {
          return { data: null, err: undefined }
        }
      }
      return { data: undefined, err: 'Unable to fetch subscription' }
    }
  },

  getBillingInvoices: async (token: string, organizationId: string) => {
    try {
      const resp: AxiosResponse<InvoicesResponse> = await axios({
        method: 'GET',
        url: `${config.apiUrl}/v1/billing/invoices/${organizationId}`,
        headers: {
          Authorization: `Bearer ${token}`
        }
      })

      return { data: resp.data, err: undefined }
    } catch (error) {
      console.log('Error: ', error instanceof Error ? error.message : error)
      return { data: undefined, err: 'Unable to fetch invoices' }
    }
  },

  getOneTimeFees: async (token: string, organizationId: string) => {
    try {
      const resp: AxiosResponse<OneTimeFeesResponse> = await axios({
        method: 'GET',
        url: `${config.apiUrl}/v1/billing/one-time-fees/${organizationId}`,
        headers: {
          Authorization: `Bearer ${token}`
        }
      })

      return { data: resp.data, err: undefined }
    } catch (error) {
      console.log('Error: ', error instanceof Error ? error.message : error)
      return { data: undefined, err: 'Unable to fetch one-time fees' }
    }
  },

  createOneTimeFeeCheckout: async (token: string, feeId: string) => {
    try {
      const resp: AxiosResponse<CheckoutSessionResponse> = await axios({
        method: 'POST',
        url: `${config.apiUrl}/v1/billing/one-time-fees:checkout`,
        headers: {
          Authorization: `Bearer ${token}`
        },
        data: { feeId }
      })

      return { data: resp.data, err: undefined }
    } catch (error) {
      console.log('Error: ', error instanceof Error ? error.message : error)
      return { data: undefined, err: 'Unable to create one-time fee checkout' }
    }
  },

  // Admin endpoints
  createOneTimeFee: async (token: string, organizationId: string, amountCents: number, label?: string, notes?: string) => {
    try {
      const resp: AxiosResponse<CreateOneTimeFeeAndProcessResponse> = await axios({
        method: 'POST',
        url: `${config.apiUrl}/v1/admin/billing/one-time-fees`,
        headers: {
          Authorization: `Bearer ${token}`
        },
        data: { organizationId, amountCents, label, notes }
      })

      return { data: resp.data, err: undefined }
    } catch (error) {
      console.log('Error: ', error instanceof Error ? error.message : error)
      return { data: undefined, err: 'Unable to create one-time fee' }
    }
  },

  createOneTimeFeeCheckoutAdmin: async (token: string, feeId: string) => {
    try {
      const resp: AxiosResponse<CheckoutSessionResponse> = await axios({
        method: 'POST',
        url: `${config.apiUrl}/v1/admin/billing/one-time-fees:checkout`,
        headers: {
          Authorization: `Bearer ${token}`
        },
        data: { feeId }
      })

      return { data: resp.data, err: undefined }
    } catch (error) {
      console.log('Error: ', error instanceof Error ? error.message : error)
      return { data: undefined, err: 'Unable to create admin checkout session' }
    }
  },

  ensureStripeCustomer: async (token: string, organizationId: string) => {
    try {
      const resp: AxiosResponse<EnsureCustomerResponse> = await axios({
        method: 'POST',
        url: `${config.apiUrl}/v1/admin/billing/ensure-customer`,
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        data: { organizationId }
      })

      return { data: resp.data, err: undefined }
    } catch (error) {
      console.log('Error: ', error instanceof Error ? error.message : error)
      return { data: undefined, err: 'Unable to ensure stripe customer' }
    }
  },

  generatePortalLink: async (token: string, organizationId: string) => {
    try {
      const resp: AxiosResponse<PortalSessionResponse> = await axios({
        method: 'POST',
        url: `${config.apiUrl}/v1/admin/billing/portal-link`,
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        data: { organizationId }
      })

      return { data: resp.data, err: undefined }
    } catch (error) {
      console.log('Error: ', error instanceof Error ? error.message : error)
      return { data: undefined, err: 'Unable to generate portal link' }
    }
  },

  getPlaybookQuotaInfo: async (token: string, organizationId: string) => {
    try {
      const resp: AxiosResponse<PlaybookQuotaInfoResponse> = await axios({
        method: 'POST',
        url: `${config.apiUrl}/v1/admin/billing/playbook-quota-info`,
        headers: {
          Authorization: `Bearer ${token}`
        },
        data: { organizationId }
      })

      return { data: resp.data, err: undefined }
    } catch (error) {
      console.log('Error: ', error instanceof Error ? error.message : error)
      return { data: undefined, err: 'Unable to fetch playbook quota info' }
    }
  },

  grantOrRevokeFreePlaybookQuota: async (
    token: string,
    organizationId: string,
    quotaAmount: number,
    notes?: string
  ) => {
    try {
      const resp: AxiosResponse<GrantFreePlaybookQuotaResponse> = await axios({
        method: 'POST',
        url: `${config.apiUrl}/v1/admin/billing/free-playbook-quota`,
        headers: {
          Authorization: `Bearer ${token}`
        },
        data: { organizationId, quotaAmount, notes }
      })

      return { data: resp.data, err: undefined }
    } catch (error) {
      console.log('Error: ', error instanceof Error ? error.message : error)
      return { data: undefined, err: 'Unable to grant or revoke free playbook quota' }
    }
  },

  getPricingModelsStats: async (token: string) => {
    try {
      const resp: AxiosResponse<PricingModelStatsItemResponse[]> = await axios({
        method: 'GET',
        url: `${config.apiUrl}/v1/admin/billing/pricing-models/stats`,
        headers: {
          Authorization: `Bearer ${token}`
        }
      })

      return { data: resp.data, err: undefined }
    } catch (error) {
      console.log('Error: ', error instanceof Error ? error.message : error)
      return { data: undefined, err: 'Unable to fetch pricing models stats' }
    }
  },

  waiveOneTimeFee: async (token: string, feeId: string, notes?: string) => {
    try {
      const resp: AxiosResponse<OperationSuccessResponse> = await axios({
        method: 'POST',
        url: `${config.apiUrl}/v1/admin/billing/one-time-fees:waive`,
        headers: {
          Authorization: `Bearer ${token}`
        },
        data: { feeId, notes }
      })

      return { data: resp.data, err: undefined }
    } catch (error) {
      console.log('Error: ', error instanceof Error ? error.message : error)
      return { data: undefined, err: 'Unable to waive one-time fee' }
    }
  },

  voidOneTimeFee: async (token: string, feeId: string, notes?: string) => {
    try {
      const resp: AxiosResponse<OperationSuccessResponse> = await axios({
        method: 'POST',
        url: `${config.apiUrl}/v1/admin/billing/one-time-fees:void`,
        headers: {
          Authorization: `Bearer ${token}`
        },
        data: { feeId, notes }
      })

      return { data: resp.data, err: undefined }
    } catch (error) {
      console.log('Error: ', error instanceof Error ? error.message : error)
      return { data: undefined, err: 'Unable to void one-time fee' }
    }
  }
}
